import { Controller } from 'react-hook-form';
import type { Control, FieldValues, FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FloatingLabelInput,
  FloatingLabelTextarea,
  FloatingLabelSelect,
  FloatingLabelDatePicker,
} from '@/shared/components/custom/form-field';
import { SelectItem } from '@/shared/components/ui/select';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import {
  parseChoiceOptions,
  parseLinearScaleOptions,
  parseGridOptions,
} from '../../adapters/field-type.adapter';
import type { FormField as FormFieldModel } from '../../types';

interface DynamicFormFieldProps<T extends FieldValues> {
  field: FormFieldModel;
  control: Control<T>;
}

export function DynamicFormField<T extends FieldValues>({
  field,
  control,
}: DynamicFormFieldProps<T>) {
  const { t } = useTranslation('forms');
  const fieldKey = field.id ?? String(field.order);
  const name = `fields.${fieldKey}` as FieldPath<T>;
  const testId = `forms-renderer-field-${fieldKey}`;

  if (field.fieldType === 'MultiSelect') {
    const options = parseChoiceOptions(field.options);
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField, fieldState }) => {
          const selected: string[] = Array.isArray(rhfField.value) ? rhfField.value : [];
          return (
            <div className="space-y-1.5" data-testid={testId}>
              <p className="text-sm font-medium">{field.label}</p>
              <div className="space-y-2">
                {options.map((option) => (
                  <label key={option} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={selected.includes(option)}
                      onCheckedChange={(checked) => {
                        rhfField.onChange(
                          checked ? [...selected, option] : selected.filter((o) => o !== option),
                        );
                      }}
                      data-testid={`${testId}-option-${option}`}
                    />
                    {option}
                  </label>
                ))}
              </div>
              {fieldState.invalid && (
                <p className="text-xs text-red-600">{t(fieldState.error?.message ?? '')}</p>
              )}
            </div>
          );
        }}
      />
    );
  }

  if (field.fieldType === 'Select') {
    const options = parseChoiceOptions(field.options);
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField, fieldState }) => (
          <div className="space-y-1.5" data-testid={testId}>
            <FloatingLabelSelect
              id={`${testId}-input`}
              label={field.label}
              value={typeof rhfField.value === 'string' ? rhfField.value : undefined}
              onValueChange={rhfField.onChange}
              hasValue={!!rhfField.value}
              invalid={fieldState.invalid}
            >
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </FloatingLabelSelect>
            {fieldState.invalid && (
              <p className="text-xs text-red-600">{t(fieldState.error?.message ?? '')}</p>
            )}
          </div>
        )}
      />
    );
  }

  if (field.fieldType === 'LinearScale') {
    const { min, max, minLabel, maxLabel } = parseLinearScaleOptions(field.options);
    const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField, fieldState }) => (
          <div className="space-y-1.5" data-testid={testId}>
            <p className="text-sm font-medium">{field.label}</p>
            <RadioGroup
              value={typeof rhfField.value === 'string' ? rhfField.value : ''}
              onValueChange={rhfField.onChange}
              className="flex items-center gap-4"
            >
              {steps.map((step) => (
                <label
                  key={step}
                  className="flex flex-col items-center gap-1 text-xs text-muted-foreground"
                >
                  {step}
                  <RadioGroupItem value={String(step)} data-testid={`${testId}-step-${step}`} />
                </label>
              ))}
            </RadioGroup>
            {(minLabel || maxLabel) && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{minLabel}</span>
                <span>{maxLabel}</span>
              </div>
            )}
            {fieldState.invalid && (
              <p className="text-xs text-red-600">{t(fieldState.error?.message ?? '')}</p>
            )}
          </div>
        )}
      />
    );
  }

  if (field.fieldType === 'Grid') {
    const { rows, columns } = parseGridOptions(field.options);
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField, fieldState }) => {
          const selections: Record<string, string> =
            typeof rhfField.value === 'string' && rhfField.value
              ? (JSON.parse(rhfField.value) as Record<string, string>)
              : {};

          function selectCell(row: string, column: string) {
            rhfField.onChange(JSON.stringify({ ...selections, [row]: column }));
          }

          return (
            <div className="space-y-1.5" data-testid={testId}>
              <p className="text-sm font-medium">{field.label}</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr>
                      <th scope="col" />
                      {columns.map((column) => (
                        <th key={column} scope="col" className="px-2 py-1 text-center font-medium">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row}>
                        <th
                          scope="row"
                          className="px-2 py-1 text-left font-normal text-muted-foreground"
                        >
                          {row}
                        </th>
                        {columns.map((column) => (
                          <td key={column} className="px-2 py-1 text-center">
                            <RadioGroup
                              value={selections[row] ?? ''}
                              onValueChange={(value) => selectCell(row, value)}
                              className="flex justify-center"
                            >
                              <RadioGroupItem
                                value={column}
                                data-testid={`${testId}-${row}-${column}`}
                                aria-label={`${row} — ${column}`}
                              />
                            </RadioGroup>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {fieldState.invalid && (
                <p className="text-xs text-red-600">{t(fieldState.error?.message ?? '')}</p>
              )}
            </div>
          );
        }}
      />
    );
  }

  if (field.fieldType === 'Date') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField, fieldState }) => (
          <div className="space-y-1.5" data-testid={testId}>
            <FloatingLabelDatePicker
              id={`${testId}-input`}
              label={field.label}
              value={typeof rhfField.value === 'string' ? rhfField.value : ''}
              onChange={rhfField.onChange}
              invalid={fieldState.invalid}
              data-testid={testId}
            />
            {fieldState.invalid && (
              <p className="text-xs text-red-600">{t(fieldState.error?.message ?? '')}</p>
            )}
          </div>
        )}
      />
    );
  }

  if (field.fieldType === 'TextArea') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField, fieldState }) => (
          <div className="space-y-1.5" data-testid={testId}>
            <FloatingLabelTextarea
              {...rhfField}
              id={`${testId}-input`}
              value={typeof rhfField.value === 'string' ? rhfField.value : ''}
              label={field.label}
              rows={4}
              invalid={fieldState.invalid}
            />
            {fieldState.invalid && (
              <p className="text-xs text-red-600">{t(fieldState.error?.message ?? '')}</p>
            )}
          </div>
        )}
      />
    );
  }

  const inputType =
    field.fieldType === 'Email'
      ? 'email'
      : field.fieldType === 'Number' || field.fieldType === 'Currency'
        ? 'number'
        : field.fieldType === 'Phone'
          ? 'tel'
          : field.fieldType === 'Time'
            ? 'time'
            : 'text';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: rhfField, fieldState }) => (
        <div className="space-y-1.5" data-testid={testId}>
          <FloatingLabelInput
            {...rhfField}
            id={`${testId}-input`}
            value={
              typeof rhfField.value === 'string' || typeof rhfField.value === 'number'
                ? rhfField.value
                : ''
            }
            type={inputType}
            label={field.label}
            invalid={fieldState.invalid}
          />
          {fieldState.invalid && (
            <p className="text-xs text-red-600">{t(fieldState.error?.message ?? '')}</p>
          )}
        </div>
      )}
    />
  );
}
